import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy } from '@angular/core';
import { AppSettings, Settings } from './app.settings';
import { Subject, Subscription, filter, takeUntil, Observable, delay, switchMap, combineLatest, firstValueFrom, map } from 'rxjs';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { LoadingService } from './core/services/loading.service';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  LogLevel,
  Logger,
} from '@azure/msal-browser';
import { Location } from '@angular/common';
import { User } from './core/models/user.model';
import { GraphService } from './core/services/graph.service';
import { CountryGlobalVariables } from './shared/constants/country-global-variables';
import { UserAuthService } from './shared/services/user-auth.service';
import { TitleService } from './core/services/title.service';
import { AppVersionService } from './core/services/app-version.service';
import { OfflineService } from './shared/services/offline.service';
import { StoreDetails } from './shared/models/stores/store-details.model';
import { Associate } from './shared/models/associates/associate.model';
import { take } from 'rxjs/operators';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { PendingDraftSnackbarComponent } from './shared/components/pending-draft-snackbar/pending-draft-snackbar.component';
import { environment } from '../environments/environment';
import { TelemetryService } from './shared/services/telemetry.service';
import { AuthType } from './shared/enum/auth-type.enum';
import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {ComponentPortal} from '@angular/cdk/portal';
import {GlobalLoadingOverlayComponent} from './shared/components/global-loading-overlay/global-loading-overlay.component';
import {DraftEntry} from './shared/models/cb-lite/db-model';
import {ScheduleFormType} from './shared/models/schedule/schedule.model';

type IdTokenClaims = {
  preferred_username: string;
  name: string;
  groups: string[];
  roles: string[];
};

const pageTitleRouteMap: Record<string, string> = {
  '/home': 'MyTeam',
  '/serviced': 'Serviced Stores',
  '/service-records': 'Service Records',
  '/service-record-store': 'Service Records',
  '/service-record-details': 'Service Record Details',
  '/non-serviced': 'Non-Serviced Stores',
  '/associates': CountryGlobalVariables.US.associates,
  '/serviced-details': 'Serviced Stores',
  '/non-serviced-details': 'Non-Serviced Stores',
  '/store-survey': 'Store Survey',
  '/store-survey/survey-details': 'Survey Details',
  '/manage-survey': 'Manage Surveys',
  '/manage-survey/survey-details': 'Survey Details',
  '/schedule-audit': 'Schedule Audit',
  '/schedule-training': CountryGlobalVariables.US.trainingSchedule,
  '/schedule-appointment': 'Schedule Event',
  '/audit': 'Start Audit',
  '/training': CountryGlobalVariables.US.trainingStart,
  '/audit-history': 'Audit History',
  '/training-history': CountryGlobalVariables.US.trainingHistory,
  '/admin': 'Admin',
  '/admin-activity': 'Admin Activity',
  '/user-activity-detail': 'Activity',
  '/user-roles': 'Override User Role',
  '/my-events': 'Events',
  '/territory-changes': 'Territory Changes',
  '/carton-put-away-scan': 'Carton Put Away',
  '/missing-box': 'Missing Box',
  '/activities': 'Activities',
  '/pos-integrity-scan-screen': 'POS Integrity Activity'
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnDestroy, AfterViewInit {
  title = 'MyTeam';
  public settings: Settings;

  isIframe: boolean;
  private loginSubscription: Subscription;
  private tokenSubscription: Subscription;
  loginDisplay: boolean;
  user: User = {
    email: '',
    displayName: '',
    groupIDs: [],
    profilePhotoUrl: null,
    currentPath: '',
    roles: [],
  };
  isLoading: Observable<boolean> = this.loadingService.isLoading.pipe(delay(0));

  public showBackToTop = false;
  private pendingDraftSnackBarRef?: MatSnackBarRef<PendingDraftSnackbarComponent>;
  private pendingDraftCheckInProgress = false;
  private loadingOverlayRef?: OverlayRef;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly loadingService: LoadingService,
    private readonly router: Router,
    private readonly graphService: GraphService,
    private readonly location: Location,
    private readonly appSettings: AppSettings,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly titleSvc: TitleService,
    private readonly userAuthService: UserAuthService,
    private readonly appVersionService: AppVersionService,
    private readonly offlineService: OfflineService,
    private readonly snackBar: MatSnackBar,
    private readonly telemetryService: TelemetryService,
    private readonly overlay: Overlay
  ) {
    this.settings = this.appSettings.settings;
    this.initViewForSafari();
    this.isIframe = window !== window.parent && !window.opener;

    this.titleSvc.titleSubject.subscribe(title => {
      if (title) {
        this.title = title;
        changeDetector.detectChanges();
      }
    });

    this.router.events.subscribe((event: Event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.loadingService.show();
          // Set the default title here (before the routed component's ngOnInit runs),
          // so a component can override it with a more specific title in ngOnInit.
          const startUrl = (event as NavigationStart).url;
          const normalizedStartUrl = String(startUrl).split('?')[0];
          this.title = this.resolvePageTitle(normalizedStartUrl);
          this.titleSvc.updateTitle(this.title);
          break;
        }

        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          const url = (event as NavigationEnd | NavigationCancel | NavigationError).url;
          const normalizedUrl = String(url).split('?')[0];
          if (event instanceof NavigationEnd && this.isDynamicFormRoute(normalizedUrl)) {
            this.dismissPendingDraftSnackbar();
          } else if (event instanceof NavigationEnd && !this.pendingDraftSnackBarRef && this.displayPendingDraft()) {
            this.checkPendingDraftInProgress();
          }
          this.loadingService.hide();
          break;
        }
        default: {
          break;
        }
      }
    });
  }

  private displayPendingDraft() {
    return this.loginDisplay || this.userAuthService.isAuthenticated();
  }

  initViewForSafari() {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    window.addEventListener('resize', () => {
      // We execute the same script as before
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    });
  }

  private resolvePageTitle(url: string): string {
    const directTitle = pageTitleRouteMap[url];
    if (directTitle) {
      return directTitle;
    }

    const matchedRoute = Object.keys(pageTitleRouteMap)
      .filter(route => route !== '/home')
      .filter(route => url === route || url.startsWith(`${route}/`))
      .sort((left, right) => right.length - left.length)[0];

    return matchedRoute ? pageTitleRouteMap[matchedRoute] : 'Events';
  }

  ngOnInit() {
    /* The manage-survey app is a separate, narrow-purpose surface hosted on its own
       domain (see environment.surveyUrl) — it doesn't need to be force-reloaded on
       every deploy, and this check has caused a reload loop there. */
    if (window.location.origin !== environment.surveyUrl) {
      this.appVersionService.checkAppVersionChange();
    }

    /* MSAL is initialized by the `initializeMsal` app initializer in AppModule, so the
       instance is guaranteed to be ready here. */
    /* AD users authenticate through a cookie and never hold an MSAL account, so an empty
       account list says nothing about them. Without this check, opening a new tab at the app
       root (where location.path() is '') pushed authenticated AD users to the MSAL landing
       page. Routing for unauthenticated users is AuthGuard's job either way. */
    if (!this.userAuthService.hasADCredentials()) {
      const acct = this.authService.instance.getAllAccounts();

      if (!acct || acct?.length === 0) {
        const redirectUrl: string = this.location.path();
        if (redirectUrl.includes('/landing') || !redirectUrl) {
          this.router.navigate(['/landing']);
        }
      }
    }

    this.isIframe = window !== window.parent && !window.opener;
    this.setLoginDisplay();

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        /* Interaction has settled, so the account cache can now be trusted. Record the
           session for the landing page's automatic sign-in. */
        this.userAuthService.rememberMsalSession();
      });

    /* Redirects are handled solely by MsalRedirectComponent (bootstrapped in AppModule and
       mounted as <app-redirect> in index.html). Calling handleRedirectObservable() here as
       well would run a second, concurrent handleRedirectPromise() and race MSAL's
       interaction lock. Post-login navigation is done by MsalLandingPageComponent's
       LOGIN_SUCCESS subscription. */

    this.registerVisibilityChangeHandler();

    this.loginSubscription = this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        console.log('login success ' + JSON.stringify(payload));

        this.authService.instance.setActiveAccount(payload.account);
        let idTokenClaims = payload.idTokenClaims as IdTokenClaims;
        this.graphService.user.email = idTokenClaims?.preferred_username;
        this.graphService.user.displayName = idTokenClaims?.name;
        this.graphService.loadProfilePhoto();

        if (idTokenClaims?.groups) {
          this.graphService.user.groupIDs = <string[]>(
            (<unknown>idTokenClaims.groups)
          );
        }
        if (idTokenClaims?.roles) {
          this.graphService.user.roles = <string[]>(
            (<unknown>idTokenClaims.roles)
          );
        }

        this.user = this.graphService.user;
        this.setLoginDisplay();
        this.registerTelemetryUser();
      });

    function loggerCallback(logLevel: LogLevel, message: string) {
      console.debug('client logging' + message);
    }

    this.authService.setLogger(
      new Logger({
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: true,
      })
    );

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        )
      )
      .subscribe((result: EventMessage) => {
        /* Report only. A failed silent token acquisition must NOT trigger logoutRedirect():
           that ends the Entra SSO session and forces a full credential prompt, when the
           expiry is purely local. Recovery is owned by AppInterceptor (which redirects on
           InteractionRequiredAuthError) and by onVisibilityChange. This event also fires on
           transient network errors, which must never sign a user out. */
        const error: string = result?.error?.message ?? 'unknown';
        console.debug('Acquire Token Failure callback', error);
        this.telemetryService.trackLoginFailure(AuthType.MSAL, error);
      });

    this.tokenSubscription = this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        )
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        console.debug('Acquire Token Success callback', payload.accessToken);

        if (this.user.displayName.length === 0) {
          let idTokenClaims = payload.idTokenClaims as IdTokenClaims;
          this.graphService.user.email = idTokenClaims?.preferred_username;
          this.graphService.user.displayName = idTokenClaims?.name;
          this.graphService.loadProfilePhoto();

          if (idTokenClaims?.groups) {
            this.graphService.user.groupIDs = <string[]>(
              (<unknown>idTokenClaims.groups)
            );
          }
          if (idTokenClaims?.roles) {
            this.graphService.user.roles = <string[]>(
              (<unknown>idTokenClaims.roles)
            );
          }

          this.user = this.graphService.user;
        }
      });

    if (this.user.displayName.length === 0 && this.loginDisplay) {
      const accounts = this.authService.instance.getAllAccounts();

      if (accounts.length > 0) {
        this.graphService.user.email =
          accounts[0].idTokenClaims['preferred_username'];
        this.graphService.user.displayName = accounts[0].idTokenClaims['name'];
        this.graphService.loadProfilePhoto();

        if (accounts[0].idTokenClaims['groups']) {
          this.graphService.user.groupIDs = <string[]>(
            (<unknown>accounts[0].idTokenClaims['groups'])
          );
        }
        if (accounts[0].idTokenClaims['roles']) {
          this.graphService.user.roles = <string[]>(
            (<unknown>accounts[0].idTokenClaims['roles'])
          );
        }

        if (!this.userAuthService.isAuthenticated()) {
          // save the state of the last url
          this.persistRedirectUri();
        }
        console.log(this.graphService.user);
        this.user = this.graphService.user;
        this.registerTelemetryUser();
      }
    }

    // Handle AD-authenticated users (cookie-based); MSAL users are covered above.
    if (this.userAuthService.hasADCredentials()) {
      this.registerTelemetryUser();
    }
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  /**
   * Resolves the current user's telemetry details from the auth layer and
   * registers them with Faro. Logs a login failure when no credentials exist.
   */
  private registerTelemetryUser(): void {
    this.userAuthService.getTelemetryUser$().subscribe((user) => {
      if (user) {
        this.telemetryService.setCurrentUser(user);
      } else {
        this.telemetryService.trackLoginFailure(
          this.userAuthService.hasADCredentials() ? AuthType.AD : AuthType.MSAL,
          'No credentials found for user'
        );
      }
    });
  }

  /**
   * Handles the tab becoming visible after a long absence (e.g. browser standby).
   * It is only for MSAL auth, the AD auth cookie tokens are managed by the AD interceptor.
   *
   * Flow on tab focus:
   *  - Token still valid  → acquireTokenSilent returns from cache instantly, no network call
   *  - Token expired, refresh token valid → acquireTokenSilent silently renews via network
   *  - Both expired → single controlled redirect to AAD, before any API calls can race to do the same
   */
  private registerVisibilityChangeHandler(): void {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private readonly onVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    /* Skip for AD auth - handled separately via cookie/refresh token interceptor */
    if (this.userAuthService.hasADCredentials()) {
      return;
    }

    const accounts = this.authService.instance.getAllAccounts();
    if (accounts.length === 0) {
      return;
    }

    const account = this.authService.instance.getActiveAccount() ?? accounts[0];
    try {
      /* forceRefresh: false → returns cached token if still valid (no network call),
         only hits the network when the token is actually expired */
      await this.authService.instance.acquireTokenSilent({
        scopes: ['user.read'],
        account,
        forceRefresh: false,
      });
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        /* Both the access token and refresh token are expired.
           Trigger one redirect now so API calls don't race to each trigger their own. */
        this.telemetryService.trackTokenRefreshRedirect(
          account.username,
          { source: 'visibility-change' }
        );
        this.authService.instance.acquireTokenRedirect({
          scopes: ['user.read'],
          account,
        });
        return;
      }

      this.telemetryService.trackTokenRefreshFailure(
        AuthType.MSAL,
        account.username,
        this.getAuthErrorMessage(error),
        { source: 'visibility-change' }
      );
    }
  };

  persistRedirectUri() {
    let redirectUri = window.location.pathname.replace('/my-team', '');
    let redirectUriParams = window.location.search;

    if (redirectUri === '/landing') {
      redirectUri = redirectUri.replace('/landing', '');
      redirectUriParams = '';
    }

    if (redirectUri !== '/') {
      localStorage.setItem('redirect-uri', redirectUri);
      localStorage.setItem('redirect-uri-params', redirectUriParams);
    }
  }

  logout(popup?: boolean) {
    this.telemetryService.trackAuthLogout(this.userAuthService.getUsername());
    this.userAuthService.logout(popup);
  }

  ngAfterViewInit() {
    this.isLoading
      .pipe(takeUntil(this._destroying$))
      .subscribe((isLoading) => this.toggleGlobalLoadingOverlay(isLoading));

    const loginComplete$ = this.userAuthService.loginComplete$.pipe(
      filter(complete => complete === true),
      take(1)
    );

    const authComplete$ = this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1)
    );

    const loadingDone$ = this.loadingService.isLoading.pipe(
      filter(isLoading => isLoading === true),
      switchMap(() =>
        this.loadingService.isLoading.pipe(
          filter(isLoading => isLoading === false),
          take(1)
        )
      ),
      take(1)
    );

    combineLatest([loadingDone$, authComplete$, loginComplete$]).pipe(
      take(1)
    ).subscribe(async () => {
      if (this.displayPendingDraft()) {
        await this.checkPendingDraftInProgress();
      }
    });
  }

  ngOnDestroy() {
    this.loadingOverlayRef?.dispose();
    // disconnect from broadcast service on component destroy
    this._destroying$.next(null);
    this._destroying$.complete();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.loginSubscription) {
      console.log('Unsubscribe Login Subscription');
      this.loginSubscription.unsubscribe();
    }
    if (this.tokenSubscription) {
      console.log('Unsubscribe Token Subscription');
      this.tokenSubscription.unsubscribe();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll($event: any) {
    $event.target.documentElement.scrollTop > 100
      ? (this.showBackToTop = true)
      : (this.showBackToTop = false);
  }

  private toggleGlobalLoadingOverlay(isLoading: boolean) {
    if (isLoading) {
      this.showGlobalLoadingOverlay();
    } else {
      this.loadingOverlayRef?.detach();
    }
  }

  private showGlobalLoadingOverlay() {
    if (!this.loadingOverlayRef) {
      this.loadingOverlayRef = this.overlay.create({
        hasBackdrop: true,
        backdropClass: 'global-loading-overlay-backdrop',
        panelClass: 'global-loading-overlay-pane',
        positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
        scrollStrategy: this.overlay.scrollStrategies.block()
      });
    }

    if (!this.loadingOverlayRef.hasAttached()) {
      this.loadingOverlayRef.attach(new ComponentPortal(GlobalLoadingOverlayComponent));
    }
  }

  public goToTop() {
    const elmnt = document.getElementById('top');
    elmnt.scrollIntoView({ behavior: 'smooth' });
  }

  async checkPendingDraftInProgress(): Promise<void> {
    if (this.pendingDraftCheckInProgress || this.pendingDraftSnackBarRef) return;
    this.pendingDraftCheckInProgress = true;
    try {
      await this.checkPendingDrafts();
    } finally {
      this.pendingDraftCheckInProgress = false;
    }
  }

  async checkPendingDrafts(): Promise<void> {
    const drafts = await this.offlineService.listDrafts();
    const draft = drafts[0];
    if (!draft) return;

    if (draft.surveyRequestId != null) {
      if (this.router.url.startsWith('/store-survey/survey-details/')) {
        return;
      }
    }

    const snackBarRef = this.snackBar.openFromComponent(PendingDraftSnackbarComponent, {
      data: {
        title: `You have unsaved changes`,
        message: `If you don't continue now, this draft will be automatically deleted.`
      },
      duration: 0,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['pending-draft-snackbar']
    });
    this.pendingDraftSnackBarRef = snackBarRef;

    const confirmed = await firstValueFrom(
      snackBarRef.afterDismissed().pipe(
        map(result => result.dismissedByAction)
      )
    );
    if (this.pendingDraftSnackBarRef === snackBarRef) {
      this.pendingDraftSnackBarRef = undefined;
    }

    if (confirmed) {
      await this.navigateToDraft(draft);
    } else {
      if (this.isTheSameSurveyPage(draft)) {
        return;
      }
      await this.offlineService.deleteDraft(draft.draftKey);
    }
  }

  private isTheSameSurveyPage(draft: DraftEntry<Record<string, any>>) {
    return draft.surveyRequestId != null
      && this.router.url.startsWith(`/store-survey/survey-details/${draft.surveyRequestId}`);
  }

  private async navigateToDraft(draft: DraftEntry): Promise<void> {
    if (draft.surveyRequestId != null) {
      await this.navigateToSurveyDraft(draft);
      return;
    }

    await this.navigateToTrainingAuditDraft(draft);
  }

  private async navigateToSurveyDraft(draft: DraftEntry): Promise<void> {
    await this.router.navigate(
      ['/store-survey/survey-details', draft.surveyRequestId],
      { state: { draft } }
    );
  }

  private async navigateToTrainingAuditDraft(draft: DraftEntry): Promise<void> {
    const navigationData: any = {
      store: { id: draft.retailerId } as StoreDetails,
      page: draft.page,
      scheduleId: draft.scheduleId,
      formType: Object.values(ScheduleFormType).find(type =>
        typeof type === 'string' && type.toLowerCase() === draft.kind),
      offlineData: {
        merchandiserId: draft.merchandiserId,
        retailerId: draft.retailerId,
        terr: draft.terr,
        response: { ...draft.data, formResponseId: draft.formResponseId },
      }
    };

    if (!draft.isAudit) {
      navigationData.associate = { id: draft.merchandiserId } as Associate;
    }
    await this.router.navigate([draft.isAudit ? 'audit' : 'training'], {
      state: { data: navigationData }
    });
  }

  private isDynamicFormRoute(url: string): boolean {
    const path = url.split('#')[0].replace(/\/$/, '');
    return path === '/audit' || path === '/training'
      || path.startsWith('/store-survey/survey-details')
      || path === '/not-found' || path === '/not-authorized'
      || path === '/landing' || path === '/ad-login';
  }

  private dismissPendingDraftSnackbar(): void {
    this.pendingDraftSnackBarRef?.dismiss();
    this.pendingDraftSnackBarRef = undefined;
  }

  private getAuthErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }
}
